import React, { useEffect, useState } from "react";
import { getData, postData } from '../middleware/apiMiddleware';
import s from './ShortReadingOverride.module.css';
import { useNavigate } from "react-router-dom";

export default function ShortReadingOverride(prop) {	
  const navigate = useNavigate();
  const itemId = prop.itemId;

  // State for all 5 fields (matches database/model)
  const [overrideItemId, setOverrideItemId] = useState(null); 
  const [paragraphStartIdx, setParagraphStartIdx] = useState(null);
  const [paragraphEndIdx, setParagraphEndIdx] = useState(null);
  const [qtnStartIdx, setQtnStartIdx] = useState(null);
  const [qtnEndIdx, setQtnEndIdx] = useState(null);

  // FETCH: Get short reading override data by itemId (1st API call)
  const fetchOverrideData = async () => {
    try {
      const res = await getData(`item/getShortReadingOverride?itemId=${itemId}`, navigate);
      const data = res.data || {};
      
      // Set state from API response
      setOverrideItemId(data.override_item_id ?? null);
      setParagraphStartIdx(data.paragraph_start_idx ?? null);
      setParagraphEndIdx(data.paragraph_end_idx ?? null);
      setQtnStartIdx(data.qtn_start_idx ?? null);
      setQtnEndIdx(data.qtn_end_idx ?? null);
    } catch (error) {
      console.error("Failed to fetch short reading override:", error);
    }
  };

  // UPDATE 1: Override Item ID (supports NULL)
  const handleUpdateOverrideItemId = async () => {
    // Validate: empty = null, else positive integer
    const value = overrideItemId === '' ? null : Number(overrideItemId);
    if (value !== null && (!Number.isInteger(value) || value < 1)) {
      alert("Please enter a positive integer for Override Item ID");
      return;
    }

    try {
      await postData("item/updateOverrideItemId", { itemId, overrideItemId: value }, navigate);
      fetchOverrideData(); // Refresh data after update
    } catch (error) {
      console.error("Failed to update Override Item ID:", error);
    }
  };

  const handleResetOverrideItemId = async () => {
    try {
      // 1. Immediately clear the input (UI feedback)
      setOverrideItemId(null);
      
      // 2. Call API to set overrideItemId = NULL
      await postData("item/updateOverrideItemId", { itemId, overrideItemId: null }, navigate);
      
      // 3. Refresh data & hide advanced fields
      fetchOverrideData();
      
    } catch (error) {
      console.error("Failed to reset Override Item ID:", error);
    }
  };

  // UPDATE 2: Paragraph Indices
  const handleUpdateParagraph = async () => {
    handleUpdateOverrideItemId();
    const start = Number(paragraphStartIdx);
    const end = Number(paragraphEndIdx);
    
    // Validate positive integers
    if (!Number.isInteger(start) || start < 1 || !Number.isInteger(end) || end < 1) {
      alert("Please enter positive integers for Paragraph Indices");
      return;
    }

    try {
      await postData("item/updateParagraphIndices", { 
        itemId, paragraph_start_idx: start, paragraph_end_idx: end 
      }, navigate);
      fetchOverrideData();
    } catch (error) {
      console.error("Failed to update Paragraph Indices:", error);
    }
  };

  // UPDATE 3: QTN (Question) Indices
  const handleUpdateQtnIdx = async () => {
    handleUpdateOverrideItemId();
    const start = Number(qtnStartIdx);
    const end = Number(qtnEndIdx);
    
    // Validate positive integers
    if (!Number.isInteger(start) || start < 1 || !Number.isInteger(end) || end < 1) {
      alert("Please enter positive integers for QTN Indices");
      return;
    }

    try {
      await postData("item/updateQtnIndices", { 
        itemId, qtn_start_idx: start, qtn_end_idx: end 
      }, navigate);
      fetchOverrideData();
    } catch (error) {
      console.error("Failed to update QTN Indices:", error);
    }
  };

  // Initial data fetch when itemId changes
  useEffect(() => {
    if (itemId) fetchOverrideData();
  }, [itemId]);

  // CONDITIONAL LOGIC: Hide Paragraph/QTN fields if overrideItemId is NULL
  const showAdvancedFields = overrideItemId !== null && overrideItemId !== '';

  return (
    <div className={s.container}>
      <h3 className={s.sectionTitle}>Short Reading Override</h3>

      {/* 1. Override Item ID (Always Visible) */}
      <div className={s.formGroup}>
        <h4 className={s.subtitle}>Override Item ID (set NULL to hide fields below)</h4>
        <div className={s.inputRow}>
          <input
            type="number"
            min="1"
            className={s.textInput}
            value={overrideItemId ?? ''}
            onChange={(e) => setOverrideItemId(e.target.value)}
            placeholder="Positive Integer (leave empty for NULL)"
          />
          <button 
            className={s.updateButton}
            onClick={handleUpdateOverrideItemId}
          >
            Update Override ID
          </button>
          <button 
            className={s.resetButton}
            onClick={handleResetOverrideItemId}
          >
            Reset to NULL
          </button>
        </div>
      </div>

      {/* 2. Paragraph Indices (VISIBLE ONLY IF overrideItemId IS NOT NULL) */}
      {showAdvancedFields && (
        <div className={s.formGroup}>
          <h4 className={s.subtitle}>Paragraph Start / End Index</h4>
          <div className={s.inputRow}>
            <input
              type="number"
              min="1"
              className={s.smallInput}
              value={paragraphStartIdx ?? ''}
              onChange={(e) => setParagraphStartIdx(e.target.value)}
              placeholder="Start (positive int)"
            />
            <input
              type="number"
              min="1"
              className={s.smallInput}
              value={paragraphEndIdx ?? ''}
              onChange={(e) => setParagraphEndIdx(e.target.value)}
              placeholder="End (positive int)"
            />
            <button 
              className={s.updateButton}
              onClick={handleUpdateParagraph}
            >
              Update Paragraph
            </button>
          </div>
        </div>
      )}

      {/* 3. QTN Indices (VISIBLE ONLY IF overrideItemId IS NOT NULL) */}
      {showAdvancedFields && (
        <div className={s.formGroup}>
          <h4 className={s.subtitle}>Question (QTN) Start / End Index</h4>
          <div className={s.inputRow}>
            <input
              type="number"
              min="1"
              className={s.smallInput}
              value={qtnStartIdx ?? ''}
              onChange={(e) => setQtnStartIdx(e.target.value)}
              placeholder="Start (positive int)"
            />
            <input
              type="number"
              min="1"
              className={s.smallInput}
              value={qtnEndIdx ?? ''}
              onChange={(e) => setQtnEndIdx(e.target.value)}
              placeholder="End (positive int)"
            />
            <button 
              className={s.updateButton}
              onClick={handleUpdateQtnIdx}
            >
              Update QTN Indices
            </button>
          </div>
        </div>
      )}
    </div>
  );
}